import { z } from "zod";
import { EventParticipantParticipantViewSchema } from "./event-participant-views.schemas.js";
import type { EventParticipant } from "./event-participant.schemas.js";
import {
  getParticipantDisplayName,
  type ParticipantNameSource,
} from "../utils/participant-name.utils.js";
import { UserDataSource } from "./user-data-item.schemas.js";

// The directory response envelope: a pure omission-only participant view plus
// the values that cannot live inside it because they are computed, not stored —
// the display-name cascade (also the server-side sort key) and the
// AI-generation flags (derived from each field's provenance `source`, which the
// view's `{ value }` projection omits).
export const DirectoryEntrySchema = z
  .object({
    entry: EventParticipantParticipantViewSchema,
    displayName: z.string(),
    isBioAIGenerated: z.boolean(),
  })
  .readonly();

export type DirectoryEntry = z.infer<typeof DirectoryEntrySchema>;

function isAISource(source: UserDataSource | undefined): boolean {
  return (
    source === UserDataSource.SYSTEM_DERIVED ||
    source === UserDataSource.ENRICHED
  );
}

// The envelope before projection: `entry` is the whole participant, which the
// transport parse narrows to the participant view.
export function buildDirectoryEntrySource<
  P extends ParticipantNameSource & Pick<EventParticipant, "bio">,
>(
  participant: P,
): {
  readonly entry: P;
  readonly displayName: string;
  readonly isBioAIGenerated: boolean;
} {
  return {
    entry: participant,
    displayName: getParticipantDisplayName(participant),
    isBioAIGenerated: isAISource(participant.bio?.source),
  };
}
